/**
 * Controls UI - Shows keyboard controls on PC, touch controls on mobile.
 * Provides a compact, collapsible overlay.
 */
export class ControlsUI {
  private container: HTMLElement;
  private isMobile: boolean;
  private isExpanded: boolean = false;

  // Touch control elements
  private leftJoystick?: HTMLElement;
  private fireButton?: HTMLElement;
  private actionButtons?: HTMLElement;
  private timeWarpBar?: HTMLElement;

  // Joystick state
  private leftJoystickData = { active: false, x: 0, y: 0 };
  private fireButtonPressed = false;
  private currentTimeScale: number = 1;

  // Callbacks for touch controls
  public onMove?: (forward: number, strafe: number, vertical: number) => void;
  public onFire?: (pressed: boolean) => void;
  public onAction?: (action: string) => void;
  public onTimeWarp?: (scale: number) => void;

  constructor(container: HTMLElement) {
    this.container = container;
    this.isMobile = this.detectMobile();

    this.createStyles();

    if (this.isMobile) {
      this.createTouchControls();
    } else {
      this.createControlsOverlay();
    }
  }

  private detectMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
           ('ontouchstart' in window);
  }

  private createStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      /* Desktop Controls Overlay */
      .controls-overlay {
        position: fixed;
        top: 10px;
        right: 10px;
        z-index: 1000;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 12px;
        user-select: none;
        pointer-events: auto;
      }

      .controls-toggle {
        background: rgba(0, 20, 30, 0.85);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 4px;
        color: #0ff;
        padding: 6px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      .controls-toggle:hover {
        background: rgba(0, 40, 50, 0.9);
        border-color: rgba(0, 255, 255, 0.5);
      }

      .controls-toggle svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
      }

      .controls-panel {
        display: none;
        background: rgba(0, 20, 30, 0.92);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 4px;
        margin-top: 6px;
        padding: 12px;
        min-width: 200px;
        max-width: 280px;
      }

      .controls-panel.expanded {
        display: block;
      }

      .controls-section {
        margin-bottom: 10px;
      }

      .controls-section:last-child {
        margin-bottom: 0;
      }

      .controls-section-title {
        color: #0ff;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 6px;
        padding-bottom: 4px;
        border-bottom: 1px solid rgba(0, 255, 255, 0.2);
      }

      .control-keys {
        display: flex;
        gap: 4px;
      }

      .control-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 3px 0;
        color: rgba(255, 255, 255, 0.8);
      }

      .control-key {
        background: rgba(0, 255, 255, 0.15);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 3px;
        padding: 2px 6px;
        font-size: 10px;
        color: #0ff;
        font-family: monospace;
        min-width: 24px;
        text-align: center;
      }

      .control-desc {
        font-size: 11px;
      }

      /* ========== MOBILE TOUCH CONTROLS ========== */

      /* Main touch container - covers full screen for layout */
      .touch-controls {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 999;
        font-family: 'Segoe UI', system-ui, sans-serif;
      }

      /* Time Warp Bar - Top of screen */
      .time-warp-bar {
        position: absolute;
        top: calc(8px + env(safe-area-inset-top, 0px));
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 3px;
        background: rgba(0, 20, 30, 0.9);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 16px;
        padding: 4px 8px;
        pointer-events: auto;
      }

      .time-warp-label {
        color: rgba(0, 255, 255, 0.7);
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-right: 2px;
      }

      .time-warp-btn {
        min-width: 32px;
        height: 24px;
        padding: 0 4px;
        background: rgba(0, 255, 255, 0.1);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 4px;
        color: rgba(0, 255, 255, 0.7);
        font-size: 9px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        touch-action: manipulation;
        transition: all 0.15s;
      }

      .time-warp-btn.active {
        background: rgba(0, 255, 255, 0.4);
        border-color: rgba(0, 255, 255, 0.8);
        color: #fff;
      }

      .time-warp-btn.pause-btn {
        background: rgba(255, 100, 50, 0.15);
        border-color: rgba(255, 100, 50, 0.4);
        color: rgba(255, 100, 50, 0.8);
        font-size: 10px;
        min-width: 28px;
      }

      .time-warp-btn.pause-btn.active {
        background: rgba(255, 100, 50, 0.5);
        border-color: rgba(255, 100, 50, 0.8);
        color: #fff;
      }

      /* Joystick - Bottom Left */
      .joystick-container {
        position: absolute;
        bottom: calc(24px + env(safe-area-inset-bottom, 0px));
        left: calc(16px + env(safe-area-inset-left, 0px));
        width: 100px;
        height: 100px;
        pointer-events: auto;
        touch-action: none;
      }

      .joystick-base {
        position: absolute;
        width: 100%;
        height: 100%;
        background: rgba(0, 255, 255, 0.08);
        border: 2px solid rgba(0, 255, 255, 0.25);
        border-radius: 50%;
        box-sizing: border-box;
      }

      .joystick-stick {
        position: absolute;
        width: 44px;
        height: 44px;
        background: rgba(0, 255, 255, 0.35);
        border: 2px solid rgba(0, 255, 255, 0.5);
        border-radius: 50%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: background 0.1s;
        touch-action: none;
        box-sizing: border-box;
      }

      .joystick-stick.active {
        background: rgba(0, 255, 255, 0.55);
        border-color: rgba(0, 255, 255, 0.8);
      }

      .joystick-label {
        position: absolute;
        top: -18px;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(0, 255, 255, 0.4);
        font-size: 8px;
        text-transform: uppercase;
        letter-spacing: 1px;
        white-space: nowrap;
      }

      /* Fire Button - Bottom Right */
      .fire-button {
        position: absolute;
        bottom: calc(24px + env(safe-area-inset-bottom, 0px));
        right: calc(16px + env(safe-area-inset-right, 0px));
        width: 72px;
        height: 72px;
        background: rgba(255, 50, 50, 0.15);
        border: 2px solid rgba(255, 50, 50, 0.4);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(255, 100, 100, 0.8);
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1px;
        pointer-events: auto;
        touch-action: none;
        box-sizing: border-box;
      }

      .fire-button.active {
        background: rgba(255, 50, 50, 0.5);
        border-color: rgba(255, 80, 80, 0.8);
        color: #fff;
      }

      /* Action Buttons - Right side, above fire button */
      .action-buttons {
        position: absolute;
        bottom: calc(110px + env(safe-area-inset-bottom, 0px));
        right: calc(16px + env(safe-area-inset-right, 0px));
        display: flex;
        flex-direction: column;
        gap: 8px;
        pointer-events: auto;
      }

      .action-btn {
        width: 44px;
        height: 44px;
        background: rgba(0, 255, 255, 0.12);
        border: 2px solid rgba(0, 255, 255, 0.35);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(0, 255, 255, 0.7);
        font-size: 9px;
        font-weight: bold;
        text-transform: uppercase;
        touch-action: none;
        box-sizing: border-box;
      }

      .action-btn.active {
        background: rgba(0, 255, 255, 0.4);
        border-color: rgba(0, 255, 255, 0.8);
        color: #fff;
      }

      /* Help Button - Top Right on mobile */
      .mobile-help-btn {
        position: absolute;
        top: calc(8px + env(safe-area-inset-top, 0px));
        right: calc(8px + env(safe-area-inset-right, 0px));
        width: 28px;
        height: 28px;
        background: rgba(0, 20, 30, 0.8);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(0, 255, 255, 0.7);
        font-size: 14px;
        font-weight: bold;
        pointer-events: auto;
        touch-action: manipulation;
      }

      .mobile-help-btn.active {
        background: rgba(0, 40, 50, 0.9);
        border-color: rgba(0, 255, 255, 0.6);
      }

      /* Mobile Help Panel */
      .mobile-help-panel {
        position: absolute;
        top: calc(44px + env(safe-area-inset-top, 0px));
        right: calc(8px + env(safe-area-inset-right, 0px));
        background: rgba(0, 20, 30, 0.95);
        border: 1px solid rgba(0, 255, 255, 0.3);
        border-radius: 8px;
        padding: 12px;
        pointer-events: auto;
        display: none;
        min-width: 180px;
      }

      .mobile-help-panel.visible {
        display: block;
      }

      .mobile-help-panel .help-title {
        color: #0ff;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 8px;
        padding-bottom: 4px;
        border-bottom: 1px solid rgba(0, 255, 255, 0.2);
      }

      .mobile-help-panel .help-row {
        display: flex;
        justify-content: space-between;
        padding: 4px 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 10px;
      }

      .mobile-help-panel .help-key {
        color: #0ff;
        font-weight: bold;
      }
    `;
    document.head.appendChild(style);
  }

  private createControlsOverlay(): void {
    const overlay = document.createElement('div');
    overlay.className = 'controls-overlay';

    const toggle = document.createElement('button');
    toggle.className = 'controls-toggle';
    toggle.innerHTML = `
      <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
      Controls
    `;
    toggle.addEventListener('click', () => this.togglePanel());
    overlay.appendChild(toggle);

    const panel = document.createElement('div');
    panel.className = 'controls-panel';
    panel.id = 'controls-panel';
    panel.innerHTML = this.getDesktopControlsHTML();

    overlay.appendChild(panel);
    this.container.appendChild(overlay);
  }

  private getDesktopControlsHTML(): string {
    return `
      <div class="controls-section">
        <div class="controls-section-title">Movement</div>
        <div class="control-row">
          <span class="control-desc">Fly Forward/Back</span>
          <span><span class="control-key">W</span> <span class="control-key">S</span></span>
        </div>
        <div class="control-row">
          <span class="control-desc">Strafe Left/Right</span>
          <span><span class="control-key">A</span> <span class="control-key">D</span></span>
        </div>
        <div class="control-row">
          <span class="control-desc">Up/Down</span>
          <span><span class="control-key">E</span> <span class="control-key">Q</span></span>
        </div>
        <div class="control-row">
          <span class="control-desc">Look Around</span>
          <span class="control-key">Mouse</span>
        </div>
      </div>

      <div class="controls-section">
        <div class="controls-section-title">Combat</div>
        <div class="control-row">
          <span class="control-desc">Fire Weapons</span>
          <span class="control-key">Space</span>
        </div>
      </div>

      <div class="controls-section">
        <div class="controls-section-title">Camera</div>
        <div class="control-row">
          <span class="control-desc">Toggle Mode</span>
          <span class="control-key">C</span>
        </div>
        <div class="control-row">
          <span class="control-desc">Zoom</span>
          <span class="control-key">Scroll</span>
        </div>
      </div>

      <div class="controls-section">
        <div class="controls-section-title">Time Warp</div>
        <div class="control-row">
          <span class="control-desc">Pause</span>
          <span class="control-key">P</span>
        </div>
        <div class="control-row">
          <span class="control-desc">Increase</span>
          <span class="control-keys"><span class="control-key">=</span> <span class="control-key">+</span></span>
        </div>
        <div class="control-row">
          <span class="control-desc">Decrease</span>
          <span class="control-keys"><span class="control-key">-</span></span>
        </div>
      </div>
    `;
  }

  private togglePanel(): void {
    this.isExpanded = !this.isExpanded;
    const panel = document.getElementById('controls-panel');
    if (panel) {
      panel.classList.toggle('expanded', this.isExpanded);
    }
  }

  private createTouchControls(): void {
    const touchControls = document.createElement('div');
    touchControls.className = 'touch-controls';

    // Time warp bar at top center
    this.createTimeWarpBar(touchControls);

    // Help button at top right
    this.createMobileHelpButton(touchControls);

    // Left joystick for movement
    this.leftJoystick = this.createJoystick();
    touchControls.appendChild(this.leftJoystick);

    // Fire button at bottom right
    this.fireButton = document.createElement('div');
    this.fireButton.className = 'fire-button';
    this.fireButton.textContent = 'FIRE';
    this.setupFireButton(this.fireButton);
    touchControls.appendChild(this.fireButton);

    // Action buttons above fire button
    this.actionButtons = document.createElement('div');
    this.actionButtons.className = 'action-buttons';

    const upBtn = this.createActionButton('UP', 'up');
    const downBtn = this.createActionButton('DN', 'down');
    const camBtn = this.createActionButton('CAM', 'camera');

    this.actionButtons.appendChild(upBtn);
    this.actionButtons.appendChild(downBtn);
    this.actionButtons.appendChild(camBtn);

    touchControls.appendChild(this.actionButtons);
    this.container.appendChild(touchControls);
  }

  private createTimeWarpBar(parent: HTMLElement): void {
    this.timeWarpBar = document.createElement('div');
    this.timeWarpBar.className = 'time-warp-bar';

    // Label
    const label = document.createElement('span');
    label.className = 'time-warp-label';
    label.textContent = 'TIME';
    this.timeWarpBar.appendChild(label);

    // Pause button
    const pauseBtn = document.createElement('div');
    pauseBtn.className = 'time-warp-btn pause-btn';
    pauseBtn.innerHTML = '&#10074;&#10074;';
    pauseBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (this.onAction) this.onAction('pause');
    }, { passive: false });
    this.timeWarpBar.appendChild(pauseBtn);

    // Speed buttons
    const speeds = [0.5, 1, 2, 4, 8];
    speeds.forEach(speed => {
      const btn = document.createElement('div');
      btn.className = 'time-warp-btn' + (speed === 1 ? ' active' : '');
      btn.textContent = speed < 1 ? '.5x' : `${speed}x`;
      btn.dataset.speed = String(speed);

      btn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.setTimeWarpSpeed(speed);
      }, { passive: false });

      this.timeWarpBar!.appendChild(btn);
    });

    parent.appendChild(this.timeWarpBar);
  }

  private createMobileHelpButton(parent: HTMLElement): void {
    const helpBtn = document.createElement('div');
    helpBtn.className = 'mobile-help-btn';
    helpBtn.textContent = '?';

    const helpPanel = document.createElement('div');
    helpPanel.className = 'mobile-help-panel';
    helpPanel.innerHTML = `
      <div class="help-title">Controls</div>
      <div class="help-row"><span>Move</span><span class="help-key">Joystick</span></div>
      <div class="help-row"><span>Look</span><span class="help-key">Drag Screen</span></div>
      <div class="help-row"><span>Fire</span><span class="help-key">FIRE Button</span></div>
      <div class="help-row"><span>Vertical</span><span class="help-key">UP / DN</span></div>
      <div class="help-row"><span>Camera</span><span class="help-key">CAM</span></div>
      <div class="help-row"><span>Time</span><span class="help-key">Top Bar</span></div>
    `;

    helpBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      helpBtn.classList.toggle('active');
      helpPanel.classList.toggle('visible');
    }, { passive: false });

    // Close when tapping outside
    document.addEventListener('touchstart', (e) => {
      if (!helpBtn.contains(e.target as Node) && !helpPanel.contains(e.target as Node)) {
        helpBtn.classList.remove('active');
        helpPanel.classList.remove('visible');
      }
    });

    parent.appendChild(helpBtn);
    parent.appendChild(helpPanel);
  }

  private setTimeWarpSpeed(speed: number): void {
    this.currentTimeScale = speed;

    if (this.timeWarpBar) {
      const buttons = this.timeWarpBar.querySelectorAll('.time-warp-btn:not(.pause-btn)');
      buttons.forEach(btn => {
        const btnSpeed = parseFloat((btn as HTMLElement).dataset.speed || '1');
        btn.classList.toggle('active', btnSpeed === speed);
      });
    }

    if (this.onTimeWarp) {
      this.onTimeWarp(speed);
    }
  }

  private createJoystick(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'joystick-container';

    const label = document.createElement('div');
    label.className = 'joystick-label';
    label.textContent = 'MOVE';

    const base = document.createElement('div');
    base.className = 'joystick-base';

    const stick = document.createElement('div');
    stick.className = 'joystick-stick';

    base.appendChild(stick);
    container.appendChild(label);
    container.appendChild(base);

    this.setupJoystick(container, stick);

    return container;
  }

  private setupJoystick(container: HTMLElement, stick: HTMLElement): void {
    const maxDistance = 28;

    const handleStart = (clientX: number, clientY: number) => {
      this.leftJoystickData.active = true;
      stick.classList.add('active');
      handleMove(clientX, clientY);
    };

    const handleMove = (clientX: number, clientY: number) => {
      if (!this.leftJoystickData.active) return;

      const rect = container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      let deltaX = clientX - centerX;
      let deltaY = clientY - centerY;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > maxDistance) {
        deltaX = (deltaX / distance) * maxDistance;
        deltaY = (deltaY / distance) * maxDistance;
      }

      stick.style.transform = `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px))`;

      this.leftJoystickData.x = deltaX / maxDistance;
      this.leftJoystickData.y = deltaY / maxDistance;

      if (this.onMove) {
        this.onMove(-this.leftJoystickData.y, this.leftJoystickData.x, 0);
      }
    };

    const handleEnd = () => {
      this.leftJoystickData.active = false;
      this.leftJoystickData.x = 0;
      this.leftJoystickData.y = 0;
      stick.classList.remove('active');
      stick.style.transform = 'translate(-50%, -50%)';

      if (this.onMove) {
        this.onMove(0, 0, 0);
      }
    };

    container.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    }, { passive: false });

    container.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }, { passive: false });

    container.addEventListener('touchend', (e) => {
      e.preventDefault();
      handleEnd();
    });

    container.addEventListener('touchcancel', handleEnd);
  }

  private setupFireButton(button: HTMLElement): void {
    const handleStart = () => {
      this.fireButtonPressed = true;
      button.classList.add('active');
      if (this.onFire) this.onFire(true);
    };

    const handleEnd = () => {
      this.fireButtonPressed = false;
      button.classList.remove('active');
      if (this.onFire) this.onFire(false);
    };

    button.addEventListener('touchstart', (e) => {
      e.preventDefault();
      handleStart();
    }, { passive: false });

    button.addEventListener('touchend', (e) => {
      e.preventDefault();
      handleEnd();
    });

    button.addEventListener('touchcancel', handleEnd);
  }

  private createActionButton(label: string, action: string): HTMLElement {
    const button = document.createElement('div');
    button.className = 'action-btn';
    button.textContent = label;

    button.addEventListener('touchstart', (e) => {
      e.preventDefault();
      button.classList.add('active');
      if (this.onAction) this.onAction(action);
    }, { passive: false });

    button.addEventListener('touchend', (e) => {
      e.preventDefault();
      button.classList.remove('active');
      if (action === 'up' || action === 'down') {
        if (this.onAction) this.onAction(action + '_release');
      }
    });

    return button;
  }

  // Public API
  public getMovementInput(): { forward: number; strafe: number; vertical: number } {
    return {
      forward: -this.leftJoystickData.y,
      strafe: this.leftJoystickData.x,
      vertical: 0
    };
  }

  public isFirePressed(): boolean {
    return this.fireButtonPressed;
  }

  public isMobileDevice(): boolean {
    return this.isMobile;
  }

  public updateTimeWarpDisplay(scale: number, isPaused: boolean): void {
    this.currentTimeScale = scale;

    if (this.timeWarpBar) {
      const buttons = this.timeWarpBar.querySelectorAll('.time-warp-btn:not(.pause-btn)');
      buttons.forEach(btn => {
        const btnSpeed = parseFloat((btn as HTMLElement).dataset.speed || '1');
        btn.classList.toggle('active', Math.abs(btnSpeed - scale) < 0.01);
      });

      const pauseBtn = this.timeWarpBar.querySelector('.pause-btn');
      if (pauseBtn) {
        pauseBtn.classList.toggle('active', isPaused);
      }
    }
  }

  public dispose(): void {
    const overlay = this.container.querySelector('.controls-overlay');
    if (overlay) overlay.remove();

    const touchControls = this.container.querySelector('.touch-controls');
    if (touchControls) touchControls.remove();
  }
}
